import { ArrowRight, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/contexts/LanguageContext";
import { HERO } from "@/constants/testIds";

const HERO_IMG =
  "https://images.pexels.com/photos/262353/pexels-photo-262353.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

const Hero = () => {
  const { t } = useLang();

  const scrollTo = (id) => () => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="home"
      data-testid={HERO.root}
      className="relative min-h-screen flex items-end pb-24 lg:pb-32 overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="International food logistics"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0E5A4E]/85 via-[#0E5A4E]/55 to-[#0E5A4E]/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0E5A4E]/40 to-transparent" />
      </div>

      <div className="container-cust relative z-10 grid lg:grid-cols-12 gap-12 pt-32">
        <div className="lg:col-span-8 animate-fade-up">
          <p className="eyebrow text-[#F0E4C3] mb-6">
            <span className="eyebrow-line bg-[#D4AF37]"></span>
            {t.hero.eyebrow}
          </p>
          <h1 className="font-serif-display text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl">
            {t.hero.title}
          </h1>
          <p className="mt-8 text-white/85 text-base md:text-lg max-w-2xl leading-relaxed">
            {t.hero.subtitle}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Button
              data-testid={HERO.ctaQuote}
              onClick={scrollTo("quote")}
              className="bg-[#D4AF37] hover:bg-[#bf9a2d] text-[#1A2421] rounded-full px-7 h-12 text-sm font-medium"
            >
              {t.hero.ctaQuote}
              <ArrowRight className="ml-1 w-4 h-4" />
            </Button>
            <Button
              data-testid={HERO.ctaCatalog}
              onClick={scrollTo("products")}
              variant="outline"
              className="bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white rounded-full px-7 h-12 text-sm font-medium"
            >
              {t.hero.ctaCatalog}
            </Button>
          </div>
        </div>

        {/* Stats card */}
        <div className="lg:col-span-4 flex lg:items-end animate-fade-up" style={{ animationDelay: "0.2s" }}>
          <div className="bg-[#FAFAF8]/95 backdrop-blur-md border border-white/40 rounded-xl p-8 w-full grid grid-cols-3 gap-4 shadow-2xl">
            <Stat value="40+" label={t.hero.stat1} />
            <Stat value="120+" label={t.hero.stat2} />
            <Stat value="15" label={t.hero.stat3} />
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 animate-bounce">
        <ChevronDown className="w-6 h-6" />
      </div>
    </section>
  );
};

const Stat = ({ value, label }) => (
  <div className="text-center">
    <div className="font-serif-display text-3xl md:text-4xl text-[#0E5A4E] leading-none">
      {value}
    </div>
    <div className="mt-2 text-[10px] md:text-xs uppercase tracking-widest text-[#4A5D58] leading-tight">
      {label}
    </div>
  </div>
);

export default Hero;
